import { useEffect, useState } from 'react';
import {
  COMMERCIAL_STATUSES,
  STATUS_LABEL,
  formatBRL,
  parseBRLToCents,
  type CommercialStatusKey,
} from '@sc/shared/domain';

export interface StatusFormValue {
  status?: CommercialStatusKey;
  closedValueCents?: number | null;
  note?: string;
}

/**
 * Troca de status comercial e valor fechado (D20), com anotação.
 * Usado no detalhe do lead no painel.
 */
export function StatusForm({
  status,
  closedValueCents,
  saving,
  error,
  saved,
  onSave,
  statusLabel = 'Status',
}: {
  status: CommercialStatusKey | null;
  closedValueCents: number | null;
  saving: boolean;
  error: string | null;
  saved: boolean;
  onSave: (v: StatusFormValue) => void;
  statusLabel?: string;
}) {
  const [nextStatus, setNextStatus] = useState<CommercialStatusKey | ''>(status ?? '');
  const [value, setValue] = useState(
    closedValueCents != null ? formatBRL(closedValueCents).replace(/^R\$\s?/, '') : '',
  );
  const [note, setNote] = useState('');
  const [valueError, setValueError] = useState<string | null>(null);

  useEffect(() => {
    setNextStatus(status ?? '');
    setValue(closedValueCents != null ? formatBRL(closedValueCents).replace(/^R\$\s?/, '') : '');
    setNote('');
    setValueError(null);
  }, [status, closedValueCents]);

  const isClosed = nextStatus === 'fechado';
  const disabled = !status;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const change: StatusFormValue = {};
    if (nextStatus && nextStatus !== status) change.status = nextStatus;
    if (isClosed) {
      const cents = parseBRLToCents(value);
      if (!cents) {
        setValueError('Informe o valor do projeto, por exemplo 18.500,00.');
        return;
      }
      if (cents !== closedValueCents) change.closedValueCents = cents;
    }
    if (note.trim()) change.note = note.trim();
    setValueError(null);
    onSave(change);
  }

  return (
    <form className="form" onSubmit={submit} noValidate>
      <div className="block">
        <label className="label" htmlFor="status-select">
          {statusLabel}
        </label>
        <select
          id="status-select"
          className="control"
          value={nextStatus}
          disabled={disabled}
          onChange={(e) => setNextStatus(e.target.value as CommercialStatusKey)}
        >
          {disabled && <option value="">Ainda não enviou o contato</option>}
          {COMMERCIAL_STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
      </div>
      {isClosed && (
        <div className="block">
          <label className="label" htmlFor="closed-value">
            Valor do projeto fechado (R$)
          </label>
          <input
            id="closed-value"
            className="control mono"
            inputMode="decimal"
            placeholder="0,00"
            value={value}
            aria-invalid={valueError ? true : undefined}
            aria-describedby={valueError ? 'closed-value-error' : undefined}
            onChange={(e) => setValue(e.target.value)}
          />
          {valueError && (
            <span id="closed-value-error" className="alert alert-error" role="alert">
              {valueError}
            </span>
          )}
        </div>
      )}
      <div className="block">
        <label className="label" htmlFor="note">
          Anotação
        </label>
        <textarea
          id="note"
          className="control"
          placeholder="Escreva uma observação sobre o contato"
          maxLength={2000}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
      {error && (
        <p className="alert alert-error" role="alert">
          {error}
        </p>
      )}
      {saved && !error && (
        <p className="alert alert-ok" role="status">
          Alterações salvas.
        </p>
      )}
      <button type="submit" className="btn btn-primary btn-block" disabled={saving || (disabled && !note.trim())}>
        {saving ? 'Salvando…' : 'Salvar alterações'}
      </button>
    </form>
  );
}
